import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
  type VisibilityState,
  type RowSelectionState,
} from '@tanstack/react-table';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';
import { Skeleton } from './Loading';
import { cn } from '../../utils/cn';
import { useAccessibility } from '@/contexts/AccessibilityContext';
import { generateId } from '@/utils/accessibility';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  onRowClick?: (row: TData) => void;
  enableRowSelection?: boolean;
  onRowSelectionChange?: (selectedRows: TData[]) => void;
  loading?: boolean;
  className?: string;
  caption?: string;
  'aria-label'?: string;
  emptyMessage?: string;
  loadingMessage?: string;
  /**
   * SPRITE-1468: the server pages the data. `data` is one page; the footer
   * and Prev/Next drive `onPageChange` instead of slicing `data` locally.
   */
  manualPagination?: {
    pageIndex: number;
    pageSize: number;
    pageCount: number;
    totalRows: number;
    onPageChange: (pageIndex: number) => void;
  };
}

export function DataTable<TData, TValue>({
  columns,
  data,
  onRowClick,
  enableRowSelection = false,
  onRowSelectionChange,
  loading = false,
  className,
  caption,
  'aria-label': ariaLabel,
  emptyMessage = 'No results found.',
  loadingMessage = 'Loading data...',
  manualPagination,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});

  const { announce } = useAccessibility();
  const tableId = generateId('table');

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    ...(manualPagination
      ? {
          manualPagination: true,
          pageCount: manualPagination.pageCount,
          onPaginationChange: (updater) => {
            const current = {
              pageIndex: manualPagination.pageIndex,
              pageSize: manualPagination.pageSize,
            };
            const next =
              typeof updater === 'function' ? updater(current) : updater;
            if (next.pageIndex !== current.pageIndex) {
              manualPagination.onPageChange(next.pageIndex);
            }
          },
        }
      : {}),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      ...(manualPagination
        ? {
            pagination: {
              pageIndex: manualPagination.pageIndex,
              pageSize: manualPagination.pageSize,
            },
          }
        : {}),
    },
    enableRowSelection,
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const totalRows = manualPagination
    ? manualPagination.totalRows
    : table.getFilteredRowModel().rows.length;
  const firstRow = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
  const lastRow = Math.min((pageIndex + 1) * pageSize, totalRows);

  // SPRITE-1468: report selection on selection changes only. The callback
  // used to be an effect dependency, so a parent passing an inline arrow
  // (Organizations) re-ran this on every render, set state with a fresh
  // array, re-rendered, and so on: "Maximum update depth exceeded", and the
  // whole app froze on that page. The latest callback is read from a ref.
  // `announce` is re-created whenever AccessibilityProvider renders, so it
  // is kept out of the dependencies the same way.
  const onRowSelectionChangeRef = React.useRef(onRowSelectionChange);
  const announceRef = React.useRef(announce);
  React.useEffect(() => {
    onRowSelectionChangeRef.current = onRowSelectionChange;
    announceRef.current = announce;
  }, [onRowSelectionChange, announce]);

  React.useEffect(() => {
    const report = onRowSelectionChangeRef.current;
    if (!report) return;
    const selectedRows = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original);
    report(selectedRows);

    const count = selectedRows.length;
    if (count > 0) {
      announceRef.current(
        `${count} row${count === 1 ? '' : 's'} selected`,
        'polite'
      );
    }
  }, [rowSelection, table]);

  React.useEffect(() => {
    if (sorting.length > 0) {
      const sortInfo = sorting[0];
      const columnName =
        (columns.find((col) => col.id === sortInfo.id)?.header as string) ||
        sortInfo.id;
      announce(
        `Table sorted by ${columnName} in ${sortInfo.desc ? 'descending' : 'ascending'} order`,
        'polite'
      );
    }
  }, [sorting, announce, columns]);

  if (loading) {
    return (
      <div
        className="space-y-4 animate-fade-in z-0 relative"
        role="status"
        aria-label={loadingMessage}
      >
        <div className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl overflow-hidden shadow-2xl relative">
          <div className="border-b border-white/5 bg-slate-800/50 p-4 backdrop-blur-md">
            <div className="flex space-x-4">
              {columns.slice(0, 4).map((_, i) => (
                <Skeleton
                  key={i}
                  width="120px"
                  height="16px"
                  className="opacity-50"
                />
              ))}
            </div>
          </div>
          <div className="p-4 space-y-4 opacity-50">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex space-x-4 items-center">
                {columns.slice(0, 4).map((_, j) => (
                  <Skeleton
                    key={j}
                    width={j === 0 ? '40px' : '120px'}
                    height={j === 0 ? '40px' : '16px'}
                    className={j === 0 ? 'rounded-full' : 'rounded-md'}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      <div className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl overflow-hidden shadow-2xl relative group pb-1">
        {/* Glow effect for table wrapper */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-indigo-500/0 group-hover:from-blue-500/5 group-hover:to-indigo-500/5 transition-all duration-700 pointer-events-none" />

        <div
          className="overflow-x-auto no-scrollbar relative z-10 w-full"
          style={{ paddingBottom: '1px' }}
        >
          <table
            id={tableId}
            className="w-full min-w-full text-left"
            role="table"
            aria-label={ariaLabel || caption || 'Data table'}
          >
            {caption && <caption className="sr-only">{caption}</caption>}
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className="border-b border-white/5 bg-slate-800/50 backdrop-blur-md"
                >
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-widest whitespace-nowrap"
                      scope="col"
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={cn(
                            'flex items-center space-x-2',
                            header.column.getCanSort() &&
                              'cursor-pointer select-none hover:text-white transition-colors'
                          )}
                          onClick={header.column.getToggleSortingHandler()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              header.column.getToggleSortingHandler()?.(e);
                            }
                          }}
                          tabIndex={header.column.getCanSort() ? 0 : -1}
                          role={
                            header.column.getCanSort() ? 'button' : undefined
                          }
                          aria-label={
                            header.column.getCanSort()
                              ? `Sort by ${flexRender(header.column.columnDef.header, header.getContext())} ${
                                  header.column.getIsSorted() === 'asc'
                                    ? 'descending'
                                    : header.column.getIsSorted() === 'desc'
                                      ? 'ascending'
                                      : 'ascending'
                                }`
                              : undefined
                          }
                        >
                          <span>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </span>
                          {header.column.getCanSort() && (
                            <span
                              className="flex flex-col ml-1"
                              aria-hidden="true"
                            >
                              <ChevronUpIcon
                                className={cn(
                                  'h-3 w-3',
                                  header.column.getIsSorted() === 'asc'
                                    ? 'text-blue-400 font-bold'
                                    : 'text-slate-600'
                                )}
                              />
                              <ChevronDownIcon
                                className={cn(
                                  'h-3 w-3 -mt-1',
                                  header.column.getIsSorted() === 'desc'
                                    ? 'text-blue-400 font-bold'
                                    : 'text-slate-600'
                                )}
                              />
                            </span>
                          )}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className={cn(
                      'border-b border-white/5 hover:bg-slate-800/50 transition-all duration-300 relative',
                      onRowClick &&
                        'cursor-pointer focus-within:bg-slate-800/50',
                      row.getIsSelected() && 'bg-blue-500/10'
                    )}
                    onClick={() => onRowClick?.(row.original)}
                    onKeyDown={(e) => {
                      if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        onRowClick(row.original);
                      }
                    }}
                    tabIndex={onRowClick ? 0 : -1}
                  >
                    {row.getIsSelected() && (
                      <td className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 rounded-r-md" />
                    )}
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-6 py-4 text-sm text-slate-200 whitespace-nowrap"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-6 py-12 text-center text-slate-500 italic"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination & Footer */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-2 pt-2 bg-transparent gap-4">
        {/* Selection info */}
        <div className="flex-1 min-w-0">
          {enableRowSelection && Object.keys(rowSelection).length > 0 ? (
            <div className="inline-flex items-center space-x-3 bg-blue-500/10 border border-blue-500/20 backdrop-blur-md rounded-lg px-4 py-2">
              <span className="text-sm font-semibold text-blue-400">
                {Object.keys(rowSelection).length} selected
              </span>
              <div className="w-px h-4 bg-blue-500/20" />
              <button
                className="text-xs font-semibold text-blue-300 hover:text-white transition-colors"
                onClick={() => {
                  setRowSelection({});
                  announce('Selection cleared', 'polite');
                }}
              >
                Clear
              </button>
            </div>
          ) : (
            <div className="text-sm text-slate-500 font-medium">
              Showing {firstRow} to {lastRow} of {totalRows}
            </div>
          )}
        </div>

        {/* Pagination nav */}
        <nav className="flex items-center space-x-3 bg-slate-900/40 p-1 rounded-xl border border-white/5 backdrop-blur-md">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="border-none bg-transparent hover:bg-white/5 disabled:opacity-30 disabled:hover:bg-transparent"
          >
            Prev
          </Button>
          <div className="px-3 text-sm font-semibold text-slate-300">
            {table.getState().pagination.pageIndex + 1}{' '}
            <span className="text-slate-500 font-normal mx-1">/</span>{' '}
            {table.getPageCount() || 1}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="border-none bg-transparent hover:bg-white/5 disabled:opacity-30 disabled:hover:bg-transparent"
          >
            Next
          </Button>
        </nav>
      </div>
    </div>
  );
}
